import { ReactNode, useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import TenantService from 'common/services/tenant'

import Backdrop from 'components/ui/backdrop'

import { setCustomHostTenant } from './custom-host'
import { isCustomDomainHost } from './host'
import { platformTenantUrl } from './url'

/**
 * Primeiros segmentos que moram so no endereco da plataforma (decisao D2 do
 * plano 538): painel, login, portal do cliente, captador e os links com token.
 * Todos dependem da sessao, que vive no `localStorage` da origem da plataforma.
 */
const PLATFORM_ONLY_SEGMENTS = new Set([
  'admin',
  'portal',
  'captador',
  'login',
  'logout',
  'change-password',
  'cadastro',
  'favorites',
  'sign',
  'upload',
  'proposta',
  'para-imobiliarias'
])

/** `true` quando o caminho precisa sair do dominio proprio para a plataforma. */
export function isPlatformOnlyPath(pathname: string): boolean {
  const segment = pathname.split('/').filter(Boolean)[0] || ''
  return PLATFORM_ONLY_SEGMENTS.has(segment.toLowerCase())
}

/** Mesmo caminho, com busca e ancora, no endereco da plataforma. */
export function platformRedirectUrl(slug: string): string {
  const { pathname, search, hash } = window.location
  return platformTenantUrl(`${pathname}${search}${hash}`, slug)
}

type Status = 'resolving' | 'ok' | 'not-found' | 'error'

interface CustomHostBootstrapProps {
  children: ReactNode
}

/**
 * Descobre de qual imobiliaria e o dominio proprio antes de montar a aplicacao
 * (Feature #538).
 *
 * Precisa vir antes do `App`, e nao dentro do router: o `App` ja busca tema e
 * autenticacao na primeira renderizacao, e essas chamadas sairiam sem
 * `X-Tenant-Slug` — ou seja, com os dados da imobiliaria original.
 *
 * Em qualquer host que nao seja dominio proprio, renderiza direto, sem chamada.
 * E quando o host nao e de ninguem, mostra "site nao encontrado" — nunca o site
 * da imobiliaria original.
 */
export default function CustomHostBootstrap({
  children
}: CustomHostBootstrapProps) {
  const custom = isCustomDomainHost()
  const [status, setStatus] = useState<Status>(custom ? 'resolving' : 'ok')

  useEffect(() => {
    if (!custom) return

    let active = true
    const host = window.location.hostname.toLowerCase()

    TenantService.findByHost(host)
      .then((tenant) => {
        if (!active) return
        if (!tenant) {
          setStatus('not-found')
          return
        }

        setCustomHostTenant({
          slug: tenant.slug,
          customDomain: tenant.customDomain || host
        })

        if (isPlatformOnlyPath(window.location.pathname)) {
          window.location.replace(platformRedirectUrl(tenant.slug))
          return
        }

        setStatus('ok')
      })
      .catch(() => {
        if (active) setStatus('error')
      })

    return () => {
      active = false
    }
  }, [custom])

  if (status === 'ok') return <>{children}</>
  if (status === 'resolving') return <Backdrop />

  const notFound = status === 'not-found'

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
        textAlign: 'center'
      }}
    >
      <Typography variant='h5' fontWeight={700}>
        {notFound ? 'Site não encontrado' : 'Não foi possível abrir o site'}
      </Typography>
      <Typography color='text.secondary' sx={{ maxWidth: 420 }}>
        {notFound ? (
          <>
            O endereço <strong>{window.location.hostname}</strong> ainda não
            está ligado a nenhuma imobiliária.
          </>
        ) : (
          'Verifique sua conexão e tente de novo em instantes.'
        )}
      </Typography>
      {!notFound && (
        <Button
          variant='contained'
          onClick={() => window.location.reload()}
          sx={{ mt: 1, borderRadius: 2 }}
        >
          Tentar de novo
        </Button>
      )}
    </Box>
  )
}
